import { create } from 'zustand';
import axios from 'axios';
import toast from 'react-hot-toast';
import { extractTenantSubdomain } from '@/utils/tenantHostname';

// Types
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  status: string;
  tenantId: string | null;
  isPlatformUser?: boolean;
  permissions?: string[];
}

export interface Tenant {
  id: string;
  name: string;
  subdomain: string;
  plan: string;
}

export interface AuthState {
  user: User | null;
  tenant: Tenant | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isLoggingIn: boolean; // Flag to prevent auto-refresh during login
}

export interface AuthActions {
  login: (email: string, password: string, tenantSubdomain: string, rememberMe?: boolean) => Promise<void>;
  logout: () => void;
  refreshAccessToken: () => Promise<void>;
  setLoading: (loading: boolean) => void;
  reinitializeAuth: () => void;
  clearAuthState: () => void;
}

type AuthStore = AuthState & AuthActions;

// API Configuration
const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:4000/api/v1';

// Add request/response logging for debugging
const DEBUG_API = process.env.NODE_ENV === 'development';

let hasShownSuspendedNotice = false;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 60 seconds for bulk operations
});

// Initialize auth from localStorage - declare early so it can be used in store
const initializeAuth = () => {
  try {
    const authData = localStorage.getItem('erpca-auth');
    if (authData) {
      const parsed = JSON.parse(authData);
      console.log('🔄 Loading auth from localStorage:', parsed);

      // Handle different data structures
      let user, tenant, accessToken, refreshToken, isAuthenticated;

      // Check if data is in nested state structure (from Zustand persist)
      if (parsed.state) {
        user = parsed.state.user;
        tenant = parsed.state.tenant;
        accessToken = parsed.state.accessToken;
        refreshToken = parsed.state.refreshToken;
        isAuthenticated = parsed.state.isAuthenticated;
      } else {
        // Flat structure
        user = parsed.user;
        tenant = parsed.tenant;
        accessToken = parsed.accessToken;
        refreshToken = parsed.refreshToken;
        isAuthenticated = parsed.isAuthenticated;
      }

      console.log('🔍 Extracted auth data:', {
        user: user ? { email: user.email, roles: user.roles } : null,
        tenant: tenant ? tenant.name : null,
        isAuthenticated,
        hasAccessToken: !!accessToken
      });

      // Validate that we have all required data for authentication
      if (isAuthenticated && user && accessToken) {
        // Check if current subdomain matches stored tenant (for tenant users)
        const currentHostname = window.location.hostname;
        const currentSubdomain = extractTenantSubdomain(currentHostname);

        // Only check subdomain mismatch if we're actually on a subdomain
        // Skip this check for plain localhost/127.0.0.1 development
        const isPlainLocalhost = currentHostname === 'localhost' || currentHostname === '127.0.0.1';
        
        // Log subdomain check details for debugging
        console.log('🔍 Subdomain check:', {
          isPlainLocalhost,
          currentHostname,
          currentSubdomain,
          tenantSubdomain: tenant?.subdomain,
          match: tenant?.subdomain === currentSubdomain
        });
        
        if (!isPlainLocalhost && tenant && currentSubdomain && tenant.subdomain && tenant.subdomain !== currentSubdomain) {
          console.warn('⚠️ Tenant subdomain mismatch detected!', {
            current: currentSubdomain,
            stored: tenant.subdomain,
            willClearAuth: true
          });
          localStorage.removeItem('erpca-auth');
          // Clear axios headers as well
          delete api.defaults.headers.common['Authorization'];
          delete api.defaults.headers.common['X-Firm'];
          useAuthStore.setState({
            user: null,
            tenant: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false
          });
          return;
        }

        // Set the auth state
        useAuthStore.setState({
          user,
          tenant,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          isLoading: false,
        });

        // Set axios headers
        api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        if (tenant) {
          api.defaults.headers.common['X-Firm'] = tenant.subdomain;
        }
        console.log('✅ Auth restored from localStorage');
      } else {
        console.log('🔄 Incomplete auth data, clearing localStorage');
        localStorage.removeItem('erpca-auth');
        useAuthStore.setState({ isLoading: false });
      }
    } else {
      console.log('📭 No auth data in localStorage');
      // Set loading to false when no auth data is found
      useAuthStore.setState({ isLoading: false });
    }
  } catch (error) {
    console.error('❌ Error loading auth from localStorage:', error);
    localStorage.removeItem('erpca-auth'); // Clear corrupted data
    // Set loading to false when there's an error
    useAuthStore.setState({ isLoading: false });
  }
};

export const useAuthStore = create<AuthStore>()((set, get) => ({
      // Initial state
      user: null,
      tenant: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true, // Start with loading true until initialization completes
      isLoggingIn: false, // Flag to prevent auto-refresh during login

      // Actions
      login: async (email: string, password: string, tenantSubdomain: string, rememberMe: boolean = false) => {
        set({ isLoading: true, isLoggingIn: true });

        try {
          // Clear any existing auth state before attempting new login
          // This prevents issues with stale refresh tokens from previous sessions
          localStorage.removeItem('erpca-auth');
          delete api.defaults.headers.common['Authorization'];
          delete api.defaults.headers.common['X-Firm'];

          // Check if this is a platform login
          const isPlatformLogin = tenantSubdomain === 'platform';

          console.log('🔐 Login attempt:', {
            email,
            tenantSubdomain,
            isPlatformLogin
          });

          const response = await api.post('/auth/login', {
            email,
            password,
            isPlatformLogin,
            rememberMe,
          }, {
            headers: isPlatformLogin ? {} : {
              'X-Firm': tenantSubdomain,
            },
          });

          if (response.data.success) {
            const { user, tenant, tokens } = response.data.data;

            set({
              user,
              tenant,
              accessToken: tokens.accessToken,
              refreshToken: tokens.refreshToken,
              isAuthenticated: true,
              isLoading: false,
              isLoggingIn: false,
            });

            // Manual persistence to localStorage
            const authData = {
              user,
              tenant,
              accessToken: tokens.accessToken,
              refreshToken: tokens.refreshToken,
              isAuthenticated: true,
            };
            localStorage.setItem('erpca-auth', JSON.stringify(authData));
            console.log('💾 Auth data saved to localStorage');

            // Set default headers for future requests
            api.defaults.headers.common['Authorization'] = `Bearer ${tokens.accessToken}`;

            // Only set tenant header for non-platform users
            if (!isPlatformLogin && tenant) {
              api.defaults.headers.common['X-Firm'] = tenantSubdomain;
            } else if (isPlatformLogin) {
              // Remove tenant header for platform users
              delete api.defaults.headers.common['X-Firm'];
            }
          } else {
            throw new Error(response.data.message || 'Login failed');
          }
        } catch (error: any) {
          set({ isLoading: false, isLoggingIn: false });

          console.error('🔐 Login error details:', {
            status: error.response?.status,
            data: error.response?.data,
            message: error.message,
            url: error.config?.url
          });

          // Handle specific error cases
          if (error.response?.data?.error?.code === 'TENANT_SUSPENDED') {
            throw new Error('Your account has been suspended. Please contact support for assistance.');
          } else if (error.response?.data?.error?.code === 'TENANT_NOT_FOUND') {
            throw new Error('Organization not found. Please check the subdomain and try again.');
          } else if (error.response?.data?.error?.message) {
            throw new Error(error.response.data.error.message);
          } else if (error.response?.data?.message) {
            throw new Error(error.response.data.message);
          } else if (error.message === 'No refresh token available') {
            throw new Error('Authentication error. Please try logging in again.');
          } else if (error.message) {
            throw new Error(error.message);
          } else {
            throw new Error('Login failed. Please try again.');
          }
        }
      },

      logout: () => {
        // Clear localStorage
        localStorage.removeItem('erpca-auth');

        hasShownSuspendedNotice = false;

        // Clear axios headers
        delete api.defaults.headers.common['Authorization'];
        delete api.defaults.headers.common['X-Firm'];

        // Reset state
        set({
          user: null,
          tenant: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          isLoggingIn: false,
        });
      },

      refreshAccessToken: async () => {
        const { refreshToken, tenant } = get();

        if (!refreshToken) {
          console.log('🔄 No refresh token available, logging out');
          get().logout();
          throw new Error('No refresh token available');
        }

        try {
          console.log('🔄 Attempting to refresh access token...');

          // Include tenant header if available for tenant-specific refresh
          const headers: any = {};
          if (tenant) {
            headers['X-Firm'] = tenant.subdomain;
          }

          const response = await api.post('/auth/refresh', {
            refreshToken,
          }, { headers });

          if (response.data.success) {
            const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data.data;

            console.log('🔄 Token refresh successful');
            const updatedState = {
              accessToken: newAccessToken,
              refreshToken: newRefreshToken || refreshToken, // Keep old refresh token if new one not provided
            };

            set(updatedState);

            // Update localStorage with new tokens
            const currentState = get();
            const authData = {
              user: currentState.user,
              tenant: currentState.tenant,
              accessToken: newAccessToken,
              refreshToken: newRefreshToken || refreshToken,
              isAuthenticated: true,
            };
            localStorage.setItem('erpca-auth', JSON.stringify(authData));

            // Update axios headers
            api.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
          } else {
            console.log('🔄 Token refresh failed - server returned success: false');
            throw new Error('Token refresh failed');
          }
        } catch (error: any) {
          console.log('🔄 Token refresh failed:', error.response?.status, error.message);
          // If refresh fails, logout user
          get().logout();
          throw error;
        }
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      clearAuthState: () => {
        // Clear localStorage
        localStorage.removeItem('erpca-auth');

        // Clear axios headers
        delete api.defaults.headers.common['Authorization'];
        delete api.defaults.headers.common['X-Firm'];

        // Reset state
        set({
          user: null,
          tenant: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          isLoggingIn: false,
        });
      },

      reinitializeAuth: () => {
        console.log('🔄 Manually reinitializing auth...');
        // Clear any existing state first
        get().clearAuthState();
        // Then reinitialize from localStorage
        initializeAuth();
      },
    }));

// Setup axios interceptors for automatic token refresh and header injection
api.interceptors.request.use(
  (config) => {
    let { accessToken, tenant } = useAuthStore.getState();

    // Fallback: Read from localStorage if store doesn't have auth data yet
    // This handles race conditions when navigating immediately after auth restoration
    if (!accessToken || !tenant) {
      try {
        const storedAuth = localStorage.getItem('erpca-auth');
        if (storedAuth) {
          const parsed = JSON.parse(storedAuth);
          accessToken = parsed.accessToken || accessToken;
          tenant = parsed.tenant || tenant;
          console.log('📦 Interceptor fallback: Read auth from localStorage', {
            hasToken: !!accessToken,
            hasTenant: !!tenant
          });
        }
      } catch (err) {
        console.error('❌ Failed to parse localStorage auth in interceptor:', err);
      }
    }

    // Always add Authorization header if token exists
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }

    // Always add X-Firm header if tenant exists
    if (tenant) {
      config.headers['X-Firm'] = tenant.subdomain;
    } else {
      // Extract tenant from current URL subdomain
      const hostname = window.location.hostname;
      const detectedSubdomain = extractTenantSubdomain(hostname);

      if (detectedSubdomain) {
        config.headers['X-Firm'] = detectedSubdomain;
      } else if (hostname === 'localhost' || hostname === '127.0.0.1') {
        // Keep a development fallback for local non-subdomain testing.
        config.headers['X-Firm'] = 'demo';
      } else {
        // Ensure base domains do not send an invalid tenant header.
        delete config.headers['X-Firm'];
      }
    }

    // Debug logging
    if (DEBUG_API) {
      console.log('🔄 API Request:', {
        method: config.method?.toUpperCase(),
        url: config.url,
        baseURL: config.baseURL,
        headers: {
          Authorization: config.headers['Authorization'] ? '***' : 'none',
          'X-Firm': config.headers['X-Firm'] || 'none'
        }
      });
    }

    return config;
  },
  (error) => {
    if (DEBUG_API) {
      console.error('❌ API Request Error:', error);
    }
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    // Debug logging for successful responses
    if (DEBUG_API) {
      console.log('✅ API Response:', {
        status: response.status,
        url: response.config.url,
        data: response.data?.success ? 'Success' : 'Data received'
      });
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle network errors gracefully - let them propagate as real errors
    if (!error.response) {
      console.log('🔄 Network error detected, API may be unavailable');
      // Don't create mock responses - let the error propagate
      return Promise.reject(error);
    }

    // Debug logging for errors
    if (DEBUG_API) {
      console.error('❌ API Error:', {
        status: error.response?.status,
        url: error.config?.url,
        message: error.response?.data?.message || error.message,
        data: error.response?.data
      });
    }

    const errorCode = error.response?.data?.error?.code;
    if (error.response?.status === 403 && errorCode === 'TENANT_SUSPENDED') {
      if (!hasShownSuspendedNotice) {
        toast.error('This tenant is suspended. Please contact your platform administrator.');
        hasShownSuspendedNotice = true;
      }

      useAuthStore.getState().logout();
      return Promise.reject(error);
    }

    // Don't retry refresh token requests or login requests to avoid infinite loops
    if (originalRequest.url?.includes('/auth/refresh') || originalRequest.url?.includes('/auth/login')) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Don't attempt refresh if we're currently logging in
      const { refreshToken, isLoggingIn } = useAuthStore.getState();
      if (isLoggingIn) {
        console.log('🔄 Currently logging in, skipping auto-refresh');
        return Promise.reject(error);
      }

      // Only attempt refresh if we have a refresh token
      if (!refreshToken) {
        console.log('🔄 No refresh token available for retry, rejecting request');
        return Promise.reject(error);
      }

      try {
        await useAuthStore.getState().refreshAccessToken();

        // Retry the original request with new token
        const { accessToken, tenant } = useAuthStore.getState();
        originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
        if (tenant) {
          originalRequest.headers['X-Firm'] = tenant.subdomain;
        }

        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, redirect to login
        console.log('🔄 Refresh failed, logging out user');
        useAuthStore.getState().logout();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);



// Call initialization
initializeAuth();

// Export the configured axios instance for use in other parts of the app
export { api };

